import React from "react";
import { motion } from "framer-motion";
import { IndexNo } from "./SectionHead";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1611581372056-30cf28a7bd2e?fm=jpg&q=80&w=1200&auto=format&fit=crop";

const PILLARS = [
  {
    title: "Experienced Facilitators",
    desc: "Seasoned lead engineers who facilitate, scribe, and mentor your team through PHA, LOPA, DHA, and SIS studies.",
  },
  {
    title: "Regulatory Expertise",
    desc: "Defensible compliance across OSHA PSM, EPA RMP, NFPA combustible dust, and IEC 61511 / ISA 84 frameworks.",
  },
  {
    title: "Practical Execution",
    desc: "Safeguards and procedures written for real operations, maintenance, and mechanical integrity work — not the audit binder.",
  },
];

const FADE = { opacity: 0, y: 14 };
const SHOW = { opacity: 1, y: 0 };
const VIEW = { once: true };
const T = { duration: 0.6 };

export const About = () => {
  return (
    <section id="about" data-testid="about-section" className="py-20 lg:py-24 bg-[#F4F6F3]">
      <div className="mx-auto max-w-[1180px] px-5 lg:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
        <motion.div
          initial={FADE}
          whileInView={SHOW}
          viewport={VIEW}
          transition={T}
          className="relative aspect-[4/5] overflow-hidden"
        >
          <img
            src={ABOUT_IMG}
            alt="Industrial refinery under daylight"
            className="w-full h-full object-cover [filter:saturate(0.85)_contrast(1.05)]"
          />
          <div className="absolute inset-0 mix-blend-multiply bg-[linear-gradient(200deg,rgba(4,22,46,0.15),rgba(11,107,68,0.32))]" />
          <div className="absolute inset-x-0 bottom-0 z-[2] bg-[#04162E]/[0.88] border-t-2 border-[#0E8A57] px-[22px] py-[18px] font-body text-[13px] text-[#C7D1DC]">
            Field-verified: procedures and safeguards validated with the operators who use them.
          </div>
        </motion.div>

        <motion.div initial={FADE} whileInView={SHOW} viewport={VIEW} transition={T}>
          <IndexNo no="03" label="Who we are" />
          <h2 className="font-display font-bold text-[26px] sm:text-[32px] lg:text-[36px] leading-[1.15] text-[#04162E] max-w-[16ch]">
            Engineering judgment, grounded in plant reality
          </h2>
          <p className="mt-[18px] font-body text-[15.5px] leading-[1.7] text-[#5C6C80] max-w-[52ch]">
            PSIEC delivers the full spectrum of process safety work — from foundational Process
            Hazard Analysis through advanced consequence modeling and facility siting — combining
            rigorous analytical method with time spent on real operating units.
          </p>
          <p className="mt-3.5 font-body text-[15.5px] leading-[1.7] text-[#5C6C80] max-w-[52ch]">
            Whether you're standing up a new PSM program, revalidating a five-year PHA cycle, or
            verifying Safety Integrity Levels, our facilitators lead your team through the study and
            hand over a report built to be acted on, not filed away.
          </p>

          <div className="mt-8">
            {PILLARS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`about-pillar-${i}`}
                className="grid grid-cols-[auto_1fr] gap-5 py-[22px] border-t border-[rgba(4,22,46,0.14)] last:border-b"
              >
                <div className="font-display text-[15px] font-bold text-[#0B6B44] pt-0.5">0{i + 1}</div>
                <div>
                  <h4 className="font-display text-[17px] font-bold text-[#04162E] mb-1.5">{p.title}</h4>
                  <p className="font-body text-[14.5px] text-[#5C6C80] leading-[1.6]">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
